import * as React from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import KeyIcon from "@mui/icons-material/Key";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAlert } from "../../contexts/AlertProvider";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { useTokens } from "../../theme";

export default function VerifyEmailForm() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [emailToken, setEmailToken] = React.useState({ emailToken: "" });
  const { alert } = useAlert();
  const navigate = useNavigate();
  const tokens = useTokens();

  const handleTokenChange = (value) => {
    setEmailToken({ emailToken: value });
  };

  const verifyToken = async () => {
    if (emailToken.emailToken === "") {
      alert({
        title: "Failed to Verify Email",
        detail: "Token is empty",
        severity: "warning",
        duration: 5000,
      });
      return;
    }

    const options = {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...emailToken }),
    };

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/agents/verify/v1`,
        options
      );

      const result = await response.json();

      if (result.status !== "SUCCESS") {
        alert({
          title: "Failed to Verify Email",
          detail: result.failureReason,
          severity: "warning",
          duration: 5000,
        });
        return;
      }

      alert({
        title: "Email Verified",
        detail: `Successfully verified account. You can now sign in.`,
        severity: "success",
        duration: 5000,
      });
      navigate("/");
    } catch (error) {
      alert({
        title: "Error Occurred",
        detail: "Unable to send verify email at the moment",
        severity: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        verifyToken();
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: "1.25rem", fontWeight: 600, color: tokens.foreground }}
        >
          Verify new account
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: tokens.mutedForeground }}>
          Paste the verification token from your email to activate your account.
        </Typography>
      </Stack>
      <TextField
        id="verify-email-token"
        label="Email Token"
        variant="outlined"
        fullWidth
        autoComplete="one-time-code"
        onChange={(event) => handleTokenChange(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <KeyIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          size="large"
          type="submit"
          fullWidth
          startIcon={<VerifiedUserOutlinedIcon />}
        >
          Confirm Verification
        </LoadingButton>
        <Button variant="outlined" size="large" fullWidth onClick={() => navigate("/")}>
          Return to Login
        </Button>
      </Stack>
    </Box>
  );
}
